import React from 'react';
import compact from 'lodash/compact';
import { useHistory } from 'react-router-dom';
import { Badge, Button, DropdownMenu, DropdownMenuItem, IconButton, PageContentHeader, Typography } from 'seagull';
import { Insight, MoreVertical } from 'seagull/icons';
import { useEventReporter, useGlobalContext, useIoContext, useStore, useStoreActions } from "../../../../contexts";
import { useCopyDirectLink } from "../../../../hooks/useCopyDirectLink";
import { useTerminateAgreement } from "../../../../hooks/useTerminateAgreement";
import { useWithdrawToEditProposal } from "../../../../hooks/useWithdrawToEditProposal";
import { handlePromise } from "../../../../infra/utils";
import { useSendReminder } from "../../../../hooks/useSendReminder";
import { useOpenEditAgreementModal } from "../../../../hooks/useOpenEditAgreementModal";
import { routes } from "../../../../constants";
import { Intent } from "../../../../infra/access-control";
import { getAgreementPdfUrl } from "../../../../business-logic/relationshipLogic";
import MinisiteTabHeader from "../../MinisiteTabHeader/MinisiteTabHeader";
import styles from "./Header.module.scss";
import { relationshipModel } from "../../../../models";
import { selectors } from "../../../../store/state-selectors";
import { useCopyProposalLink } from "../../../../hooks/useCopyProposalLink";
const testPrefix = 'agreement-minisite-topbar-';
const agreementStatusBadgeMap = {
  pending: 'Pending',
  terminated: 'Terminated',
  canceled: 'Canceled'
};
export default function Header(_ref) {
  var _userInfo$worksAt;
  let {
    relationship,
    onUpdate
  } = _ref;
  const userInfo = useStore(state => state.userInfo);
  const {
    logBI
  } = useStoreActions();
  const pdfHost = useStore(selectors.pdfHost);
  const {
    reportPopupAction
  } = useEventReporter();
  const {
    withdrawToEditProposalByIdWithConfirmation
  } = useWithdrawToEditProposal();
  const userIsVendor = relationship.vendor.id === (userInfo === null || userInfo === void 0 || (_userInfo$worksAt = userInfo.worksAt) === null || _userInfo$worksAt === void 0 ? void 0 : _userInfo$worksAt.id);
  const agreementStatusString = agreementStatusBadgeMap[relationship.status];
  const {
    terminateAgreementById,
    cancelProposalById
  } = useTerminateAgreement();
  const openEditModeModal = useOpenEditAgreementModal();
  const {
    copyDirectLink
  } = useCopyDirectLink();
  const {
    sendReminder
  } = useSendReminder();
  const globalContext = useGlobalContext();
  const {
    copyToClipboard,
    openNewTab
  } = useIoContext();
  const history = useHistory();
  const {
    getAgreementNotificationLink
  } = useCopyProposalLink();
  const overflowMenuActions = compact([relationshipModel.isPending(relationship) && userIsVendor && {
    intent: Intent.SendProposalReminder,
    text: 'Resend link to customer',
    testid: 'resend',
    onClick: () => sendReminder(relationship)
  }, relationshipModel.isPending(relationship) && userIsVendor && {
    intent: Intent.CopyRelationshipDirectLink,
    text: 'Copy agreement link',
    testid: 'copy',
    onClick: async () => {
      const {
        vendor,
        customer,
        progressStatus
      } = relationship;
      logBI(10586, {
        vendorId: vendor.id,
        clientId: customer.id,
        agreementStatus: progressStatus
      });
      globalContext.startLoader();
      const {
        success,
        data
      } = await handlePromise(getAgreementNotificationLink(relationship.id));
      globalContext.stopLoader();
      if (success) {
        try {
          // This will fail if the document loses focus.
          copyToClipboard(data);
          globalContext.showSuccessToast('Link copied to clipboard');
        } catch (err) {
          globalContext.showErrorToast('Failed to copy link to clipboard');
        }
      }
    }
  }, relationshipModel.isPending(relationship) && userIsVendor && {
    intent: Intent.CancelProposal,
    text: 'Cancel this agreement',
    testid: 'cancel',
    onClick: () => {
      cancelProposalById(relationship.id, () => {
        history.push(routes.home);
      }).catch(err => {
        console.log(err);
      });
    }
  }, userIsVendor && {
    intent: Intent.CopyRelationshipDirectLink,
    text: 'Copy link',
    testid: 'copy',
    onClick: () => {
      copyDirectLink(relationship.id, {
        type: 'agreement',
        id: relationship.id
      });
      logBI(14000, userInfo === null || userInfo === void 0 ? void 0 : userInfo.email);
    }
  }, (relationshipModel.isApproved(relationship) || relationshipModel.isTerminated(relationship)) && {
    intent: Intent.DownloadAgreementPdf,
    text: 'Download PDF',
    testid: 'download-pdf',
    onClick: () => openNewTab(getAgreementPdfUrl(relationship.id, pdfHost))
  }, relationshipModel.isApproved(relationship) && userIsVendor && {
    intent: Intent.TerminateAgreement,
    text: 'Terminate this agreement',
    testid: 'terminate',
    onClick: () => {
      reportPopupAction('terminate-agreement-modal');
      terminateAgreementById({
        id: relationship.id,
        postAction: () => onUpdate()
      });
    }
  }]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MinisiteTabHeader, {
    actions: /*#__PURE__*/React.createElement("div", {
      className: styles.actions
    }, agreementStatusString && /*#__PURE__*/React.createElement(Badge, {
      size: "big",
      skin: "warning",
      "data-testid": testPrefix + 'badge-status',
      "data-status": relationship.status
    }, agreementStatusString), userIsVendor && !relationshipModel.isTerminated(relationship) && /*#__PURE__*/React.createElement(Button, {
      onClick: () => {
        if (relationshipModel.isPending(relationship)) {
          withdrawToEditProposalByIdWithConfirmation(relationship.id);
        } else {
          openEditModeModal({
            relationship,
            onUpdate
          });
        }
      },
      "data-testid": testPrefix + 'btn-edit-sow',
      disabled: relationship.isSample
    }, "Edit"), overflowMenuActions.length > 0 && /*#__PURE__*/React.createElement(DropdownMenu, {
      target: /*#__PURE__*/React.createElement(IconButton, {
        "data-testid": testPrefix + 'vendor-overflow-button'
      }, /*#__PURE__*/React.createElement(MoreVertical, null))
    }, overflowMenuActions.map(_ref2 => {
      let {
        intent,
        text,
        testid,
        onClick
      } = _ref2;
      return /*#__PURE__*/React.createElement(DropdownMenuItem, {
        intent: intent,
        "data-testid": "".concat(testPrefix, "overflow-").concat(testid),
        key: text,
        onClick: onClick
      }, text);
    })))
  }), relationship.isSample && /*#__PURE__*/React.createElement(PageContentHeader, null, /*#__PURE__*/React.createElement("div", {
    "data-testid": testPrefix + 'sample-banner',
    className: styles.sampleAgreementBanner
  }, /*#__PURE__*/React.createElement(Insight, null), /*#__PURE__*/React.createElement(Typography, {
    variant: "paragraph2",
    weight: "light",
    className: styles.sampleAgreementBannerText
  }, "You're currently reviewing a test agreement. Feel free to play around, nothing will be saved"))));
}